import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Message, Channel } from '../../types';
import { MessageItem } from './MessageItem';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { Send, MessageSquare, Info, Users, Plus, Lock, Zap } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';

interface ChatWindowProps {
  selectedChannel: Channel | null;
  messages: Message[];
  isLoadingMessages: boolean;
  messageText: string;
  onMessageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendMessage: (e?: React.FormEvent) => void;
  onUpdateMessage: (id: string, body: string) => void;
  onDeleteMessage: (id: string) => void;
  replyTo: Message | null;
  setReplyTo: (msg: Message | null) => void;
  presenceCount: number;
  typingUsers: Record<string, string>;
  isConnected: boolean;
  currentUserId?: string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isLocked?: boolean;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  selectedChannel,
  messages,
  isLoadingMessages,
  messageText,
  onMessageChange,
  onSendMessage,
  onUpdateMessage,
  onDeleteMessage,
  replyTo,
  setReplyTo,
  presenceCount,
  typingUsers,
  isConnected,
  currentUserId,
  scrollRef,
  isLocked = false
}) => {
  const navigate = useNavigate();
  // Grouping messages
  const groupedMessages = React.useMemo(() => {
    const groups: Message[][] = [];
    let currentGroup: Message[] = [];
    
    // Reverse because useMessages returns newest first
    const sorted = [...messages].reverse();

    sorted.forEach((msg, i) => {
      if (i === 0 || msg.senderUserId !== sorted[i - 1].senderUserId) {
        if (currentGroup.length > 0) groups.push(currentGroup);
        currentGroup = [msg];
      } else {
        // Also break group if time difference is large (e.g. > 5 mins)
        const prevTime = new Date(sorted[i - 1].createdAt || Number.MAX_SAFE_INTEGER).getTime();
        const currTime = new Date(msg.createdAt || Number.MAX_SAFE_INTEGER).getTime();
        if (currTime - prevTime > 5 * 60 * 1000) {
          groups.push(currentGroup);
          currentGroup = [msg];
        } else {
          currentGroup.push(msg);
        }
      }
    });
    if (currentGroup.length > 0) groups.push(currentGroup);
    return groups;
  }, [messages]);

  if (!selectedChannel) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-300 mb-4 dark:bg-slate-800">
          <MessageSquare size={32} />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1 dark:text-white">Welcome to Chat</h2>
        <p className="text-sm text-gray-500 max-w-xs">
          Select a channel from the sidebar to start collaborating with your team.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-slate-950">
      {/* Header */}
      <div className="h-14 px-6 bg-white border-b border-gray-200 flex items-center justify-between flex-shrink-0 dark:bg-slate-900">
        <div className="flex items-center gap-3 min-w-0">
          <h2 className="font-semibold text-gray-900 truncate dark:text-white">
            {selectedChannel.channelType === 'GROUP' ? `#${selectedChannel.name}` : selectedChannel.name}
          </h2>
          {!isLocked && (
            <>
              <div className="h-4 w-px bg-gray-200 hidden sm:block" />
              <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500">
                <Users size={14} />
                <span>{presenceCount} active</span>
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
            <Info size={20} />
          </button>
        </div>
      </div>

      {isLocked ? (
        <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden bg-slate-50 dark:bg-slate-950">
          {/* Decorative gradients */}
          <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md w-full bg-white/80 backdrop-blur-md border border-gray-200 rounded-3xl p-8 text-center shadow-xl dark:bg-slate-900/80 dark:border-slate-800 relative z-10"
          >
            <div className="mb-6 relative inline-block">
              {/* Animated outer ring */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-brand-primary to-indigo-500 animate-pulse blur-sm opacity-50" />
              <div className="w-16 h-16 bg-gradient-to-tr from-brand-primary to-indigo-600 text-white rounded-2xl flex items-center justify-center relative shadow-lg">
                <Lock size={28} className="animate-bounce" />
              </div>
            </div>
            
            <h3 className="text-xl font-extrabold text-gray-900 mb-3 tracking-tight dark:text-white flex items-center justify-center gap-2">
              <Zap size={20} className="text-brand-primary fill-brand-primary" />
              Unlock Real-Time Chat
            </h3>
            
            <p className="text-sm text-gray-600 mb-6 leading-relaxed dark:text-gray-300">
              Real-time WebSocket chat, direct messages, and channel collaboration are exclusive to the <strong className="text-brand-primary">Momentum</strong> and <strong className="text-indigo-600">Institutional</strong> subscription tiers.
            </p>
            
            <div className="space-y-3">
              <Button 
                variant="primary" 
                onClick={() => navigate('/settings/billing')}
                className="w-full h-12 text-sm font-bold shadow-lg shadow-brand-primary/20 hover:scale-[1.02] transition-transform duration-200"
              >
                Upgrade Plan
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/plans')}
                className="w-full text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                Compare Plan Features
              </Button>
            </div>
          </motion.div>
        </div>
      ) : (
        <>
          {/* Messages Area */}
          <div 
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-6 space-y-6"
          >
            {isLoadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="md" />
              </div>
            ) : groupedMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-2">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 dark:bg-slate-800">
                  <MessageSquare size={24} />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">No messages yet</h3>
                <p className="text-xs text-gray-500">Be the first to start the conversation!</p>
              </div>
            ) : (
              groupedMessages.map((group, idx) => (
                <MessageItem
                  key={idx}
                  messages={group}
                  isOwn={group[0].senderUserId === currentUserId || group[0].senderUserId === 'me'}
                  onReply={setReplyTo}
                  onEdit={onUpdateMessage}
                  onDelete={onDeleteMessage}
                  allMessages={messages}
                />
              ))
            )}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200 dark:bg-slate-900">
            {replyTo && (
              <div className="mb-2 p-2 bg-gray-50 rounded-lg flex items-center justify-between border border-gray-100 animate-in slide-in-from-bottom-2 duration-200 dark:bg-slate-950">
                <div className="flex items-center gap-2 overflow-hidden">
                  <div className="w-1 h-8 bg-brand-primary rounded-full" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Replying to {replyTo.senderName}</p>
                    <p className="text-xs text-gray-600 truncate">{replyTo.body}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setReplyTo(null)}
                  className="p-1 hover:bg-gray-200 rounded-full text-gray-400"
                >
                  <Plus className="rotate-45" size={14} />
                </button>
              </div>
            )}
            <form onSubmit={onSendMessage} className="flex gap-2">
              <Input
                value={messageText}
                onChange={onMessageChange}
                placeholder={`Message ${selectedChannel.channelType === 'GROUP' ? '#' : ''}${selectedChannel.name}`}
                className="flex-1"
                autoComplete="off"
              />
              <Button 
                type="submit" 
                variant="primary" 
                size="md"
                disabled={!messageText.trim()}
                className="px-3"
              >
                <Send size={18} />
              </Button>
            </form>
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-1.5 h-4">
                {Object.keys(typingUsers).length > 0 && (
                  <div className="flex items-center gap-2 text-[10px] text-gray-500 italic">
                    <div className="flex gap-0.5">
                      <motion.div animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 1 }} className="w-1 h-1 bg-gray-400 rounded-full" />
                      <motion.div animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} className="w-1 h-1 bg-gray-400 rounded-full" />
                      <motion.div animate={{ opacity: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} className="w-1 h-1 bg-gray-400 rounded-full" />
                    </div>
                    <span>
                      {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <div className={cn(
                  "w-2 h-2 rounded-full",
                  isConnected ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" : "bg-red-500"
                )} />
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-mono">
                  {isConnected ? 'Connected' : 'Reconnecting...'}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

